// M16 T15 — AI Kontrolü paneli (mock 2 düzeni): çalıştır butonu + 6 kalem checklist (✓/⚠,
// genişleyebilir not) + gösterge linkli uyarı listesi + son kontrol zamanı. Özet sağ rail'de
// ve import sihirbazı 3. adımında kullanılır.
// Tur bulguları: (B-17) uyarıda kod yerine ETİKET + işletme adı (kod `title`'da); (P3-24) `compact`
// (import önizlemesi) modunda set'in ESKİ sonucu otomatik yüklenmez — panel yalnız bu bağlamda
// koşulan kontrolü gösterir; "Son kontrol" vurgulu.
import { useCallback, useEffect, useState } from "react";
import { getLatestKpiAiCheck, runKpiAiCheck, type KpiAiCheckRecord } from "../../api/kpi-checks";
import { ApiError } from "../../api/client";
import { KPI_CHECKS_COPY as C } from "../../lib/kpi-checks-copy";

interface Props {
  setId: string | null;
  canRun: boolean;
  years?: number[] | undefined;
  /** Uyarıdaki göstergeye git (Özet: Veri Girişi highlight). */
  onOpenIndicator?: ((code: string) => void) | undefined;
  /** import önizlemesi: dar düzen + önceki sonucu yükleme (yalnız bu koşum) */
  compact?: boolean | undefined;
  /** kod → etiket (Özet verir); yoksa kod yazılır */
  labelByCode?: Record<string, string> | undefined;
  /** işletme id → ad (çok işletmeli müşteride uyarı satırına eklenir) */
  entityNameById?: Record<string, string> | undefined;
}

export function AiCheckPanel({
  setId,
  canRun,
  years,
  onOpenIndicator,
  compact = false,
  labelByCode,
  entityNameById,
}: Props): JSX.Element {
  const [check, setCheck] = useState<KpiAiCheckRecord | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setCheck(null);
    setError(null);
    if (!setId || compact) return; // compact: eski sonuç gösterilmez (P3-24)
    let alive = true;
    getLatestKpiAiCheck(setId)
      .then((c) => alive && setCheck(c))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [setId, compact]);

  const run = useCallback(async () => {
    if (!setId) return;
    setRunning(true);
    setError(null);
    try {
      setCheck(await runKpiAiCheck(setId, years));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : C.aiFailed);
    } finally {
      setRunning(false);
    }
  }, [setId, years]);

  return (
    <section
      aria-label={C.aiTitle}
      className="rounded-xl p-3 text-sm"
      style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="font-semibold" style={{ color: "var(--color-text)" }}>
          {C.aiTitle}
        </h3>
        {canRun && (
          <button
            type="button"
            onClick={() => void run()}
            disabled={!setId || running}
            className="rounded-lg px-3 py-1.5 text-xs font-medium"
            style={{
              background: "var(--color-primary)",
              color: "var(--color-surface)",
              opacity: !setId || running ? 0.6 : 1,
            }}
          >
            {running ? C.aiRunning : C.aiRun}
          </button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-xs mb-2" style={{ color: "var(--color-warning-text)" }}>
          {error}
        </p>
      )}
      {!check ? (
        <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
          {C.aiNever}
        </p>
      ) : (
        <>
          <ul className="flex flex-col gap-1">
            {check.checklist.map((item) => {
              const expanded = open[item.key] === true;
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 text-left"
                    onClick={() => item.note && setOpen((o) => ({ ...o, [item.key]: !expanded }))}
                    aria-expanded={item.note ? expanded : undefined}
                  >
                    <span
                      aria-hidden
                      style={{
                        color: item.ok ? "var(--color-success)" : "var(--color-warning-text)",
                      }}
                    >
                      {item.ok ? "✓" : "⚠"}
                    </span>
                    <span style={{ color: "var(--color-text)" }}>{C.checklist[item.key]}</span>
                    {item.note && (
                      <span className="ml-auto text-xs" style={{ color: "var(--color-text-3)" }}>
                        {expanded ? "▴" : "▾"}
                      </span>
                    )}
                  </button>
                  {expanded && item.note && (
                    <p className="text-xs pl-6" style={{ color: "var(--color-text-2)" }}>
                      {item.note}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          {!compact && (
            <div className="mt-3">
              <div className="text-xs font-medium mb-1" style={{ color: "var(--color-text-2)" }}>
                {C.aiWarnings} ({check.warnings.length})
              </div>
              {check.warnings.length === 0 ? (
                <p className="text-xs" style={{ color: "var(--color-text-3)" }}>
                  {C.aiNoWarnings}
                </p>
              ) : (
                <ul className="flex flex-col gap-1 text-xs">
                  {check.warnings.slice(0, 20).map((w, i) => {
                    const label = labelByCode?.[w.indicatorCode] ?? w.indicatorCode;
                    const entity = w.entityId ? entityNameById?.[w.entityId] : undefined;
                    return (
                      <li
                        key={`${w.rule}-${w.indicatorCode}-${w.entityId ?? ""}-${w.year ?? ""}-${i}`}
                      >
                        <button
                          type="button"
                          className="text-left hover:underline"
                          onClick={() => onOpenIndicator?.(w.indicatorCode)}
                          title={w.indicatorCode}
                        >
                          <span
                            className="rounded px-1 mr-1"
                            style={{
                              background: "var(--color-warning-bg)",
                              color: "var(--color-warning-text)",
                            }}
                          >
                            {C.rules[w.rule]}
                          </span>
                          <span style={{ fontWeight: 600 }}>{label}</span>
                          {entity ? ` · ${entity}` : ""}
                          {w.year ? ` · ${w.year}` : ""} — {w.message}
                        </button>
                      </li>
                    );
                  })}
                  {check.warnings.length > 20 && (
                    <li style={{ color: "var(--color-text-3)" }}>
                      +{check.warnings.length - 20} daha
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
          <p className="text-xs mt-2" style={{ color: "var(--color-text-3)" }}>
            {C.aiLast}:{" "}
            <b style={{ color: "var(--color-text-2)" }}>
              {new Date(check.checkedAt).toLocaleString("tr-TR")}
            </b>
          </p>
        </>
      )}
    </section>
  );
}
